import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton, DataTable } from "../../components/modern";
import type { Column } from "../../components/modern";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../utils/permissions";
import { adminApi } from "../../utils/api";
import { showSuccessToast, showErrorToast } from "../../utils/toastNotifications";

export const CustomerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, i18n } = useTranslation('customers');
  const isRTL = i18n.language === 'ar';

  const [customer, setCustomer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<any[]>([]);

  // Create Order
  const [formData, setFormData] = useState([
    {
      key: Date.now(),
      link: "",
      description: "",
      quantity: 1,
      itemPrice: 0,
      originCountryId: 0,
      userId: id
    },
  ]);
  const [countries, setCountries] = useState<any[]>([]);
  const [isShipForMe, setIsShipForMe] = useState(false);

  // Handle input change
  const handleInputChange = (index: number, event: any) => {
    const { name, value } = event.target;
    if (name == "itemPrice" && value < 0) {
      return;
    }
    const updatedFormData = [...formData];
    updatedFormData[index][name] = value;
    setFormData(updatedFormData);
  };

  // Handle quantity change
  const handleQuantityChange = (index: number, increment: number) => {
    const updatedFormData = [...formData];
    const newQuantity = updatedFormData[index].quantity + increment;
    updatedFormData[index].quantity = newQuantity > 1 ? newQuantity : 1; // Ensure quantity doesn't go below 1
    setFormData(updatedFormData);
  };

  // Add a new row
  const addNewRow = () => {
    setFormData([
      ...formData,
      {
        key: Date.now(),
        link: "",
        description: "",
        quantity: 1,
        itemPrice: 0,
        originCountryId: 0,
        userId: id
      },
    ]);
  };

  // Delete a row
  const deleteRow = (key: number) => {
    setFormData(formData.filter((item) => item.key !== key));
  };

  // Fetch countries list
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await adminApi.call('/country', 'GET');

        if (!response.success) {
          showErrorToast(response.error || "Failed to fetch countries");
          return;
        }

        setCountries(response.data?.items.filter((x: any) => x.countryType == 2 && !x.isSuspended) || []);
      } catch (error: any) {
        showErrorToast("Error: " + error.message);
      }
    };

    fetchCountries();
  }, []);

  // Submit order
  const submitOrder = async () => {
    setLoading(true);

    try {
      const controller = isShipForMe ? "shipforme" : "order"
      const response = await adminApi.call(`/${controller}`, 'POST', { Orders: formData });

      if (!response.success) {
        showErrorToast(response.error || "Failed to create order");
        return;
      }

      showSuccessToast("Order created successfully!");
      setFormData([
        {
          key: Date.now(),
          link: "",
          description: "",
          quantity: 1,
          itemPrice: 0,
          originCountryId: 0,
          userId: id
        },
      ]); // Reset form
    } catch (error: any) {
      showErrorToast("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch customer details
  const fetchCustomer = async () => {
    try {
      const response = await adminApi.call(`/customer/${id}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('messages.fetchError'));
        return;
      }

      const data = response.data;
      setCustomer(data);
      console.log(data);

      // Combine orders and shipForMes, then sort by createdAt desc
      const allOrders = [
        ...(data.orders ?? []),
        ...(data.shipForMes ?? [])
      ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setOrders(allOrders);
    } catch (error: any) {
      showErrorToast(t('messages.fetchError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && token) {
      fetchCustomer();
    }
  }, [id, token]);

  const getStatusBadge = (status: number) => {
    const statusMap: { [key: number]: { text: string; color: string } } = {
      0: { text: "Pending", color: "secondary" },
      1: { text: "Validated", color: "info" },
      2: { text: "Rejected", color: "danger" },
      3: { text: "Valid For Shipping", color: "primary" },
      4: { text: "Rejected By Shipping", color: "danger" },
      5: { text: "Calculated", color: "secondary" },
      6: { text: "Approved", color: "success" },
      7: { text: "Approved By Customer", color: "success" },
      8: { text: "Rejected By Customer", color: "danger" },
      9: { text: "Payment Under Collecting", color: "warning" },
      10: { text: "Purchase Under Process", color: "warning" },
      11: { text: "On Hold", color: "dark" },
      12: { text: "Cancelled", color: "danger" },
      13: { text: "Order Purchased", color: "info" },
      14: { text: "In Transit To Origin Warehouse", color: "primary" },
      15: { text: "Received At Warehouse", color: "success" },
      16: { text: "Lost/Damaged", color: "danger" },
      17: { text: "Delayed", color: "warning" },
      18: { text: "Refunded", color: "info" },
      19: { text: "Reordered", color: "secondary" },
      20: { text: "Shipped", color: "primary" },
      21: { text: "Arrived at Destination Country", color: "success" },
      22: { text: "Under Customs Clearance", color: "warning" },
      23: { text: "Released From Customs", color: "info" },
      24: { text: "Out For Delivery", color: "primary" },
      25: { text: "Delivered", color: "success" },
      26: { text: "Delivery Failed", color: "danger" },
      27: { text: "Out For Delivery (Rescheduled)", color: "warning" },
      28: { text: "Returned To Sender", color: "danger" },
    };

    const statusInfo = statusMap[status] || {
      text: "Unknown",
      color: "secondary",
    };
    return (
      <span className={`badge bg-${statusInfo.color}`}>{statusInfo.text}</span>
    );
  };

  // Define table columns for orders
  const orderColumns: Column[] = [
    {
      key: 'id',
      title: t('ordersTable.orderNumber'),
      sortable: true,
      filterable: true,
      width: '100px',
      render: (value) => <span className="fw-bold fs-5">#{value}</span>
    },
    {
      key: 'orderType',
      title: t('ordersTable.type'),
      sortable: true,
      filterable: true,
      filterValue: (row) => row.orderType == 0 ? t('buy4me') : t('ship4me'),
      width: '120px',
      render: (value) => (
        <span className={`badge bg-${value == 0 ? "primary" : "warning"} px-3 py-2 fs-6`}>
          <i className={`fa ${value == 0 ? 'fa-shopping-cart' : 'fa-ship'} ${isRTL ? 'ms-1' : 'me-1'}`}></i>
          {value == 0 ? t('buy4me') : t('ship4me')}
        </span>
      )
    },
    {
      key: 'orderGroup',
      title: t('ordersTable.group'),
      sortable: true,
      filterable: true,
      width: '120px'
    },
    {
      key: 'merchantName',
      title: t('ordersTable.merchant'),
      sortable: true,
      filterable: true,
      width: '200px'
    },
    {
      key: 'quantity',
      title: t('ordersTable.quantity'),
      sortable: true,
      filterable: true,
      width: '100px',
      render: (value) => <span className="badge bg-secondary fs-6 px-3 py-2">{value}</span>
    },
    {
      key: 'orderStatus',
      title: t('ordersTable.status'),
      sortable: true,
      filterable: true,
      filterValue: (row) => {
        const statusMap: { [key: number]: string } = {
          0: "Pending", 1: "Validated", 2: "Rejected", 3: "Valid For Shipping",
          4: "Rejected By Shipping", 5: "Calculated", 6: "Approved", 7: "Approved By Customer",
          8: "Rejected By Customer", 9: "Payment Under Collecting", 10: "Purchase Under Process",
          11: "On Hold", 12: "Cancelled", 13: "Order Purchased", 14: "In Transit To Origin Warehouse",
          15: "Received At Warehouse", 16: "Lost/Damaged", 17: "Delayed", 18: "Refunded",
          19: "Reordered", 20: "Shipped", 21: "Arrived at Destination Country",
          22: "Under Customs Clearance", 23: "Released From Customs", 24: "Out For Delivery",
          25: "Delivered", 26: "Delivery Failed", 27: "Out For Delivery (Rescheduled)",
          28: "Returned To Sender"
        };
        return statusMap[row.orderStatus] || "Unknown";
      },
      width: '150px',
      render: (value) => getStatusBadge(value)
    },
    {
      key: 'itemPrice',
      title: t('ordersTable.price'),
      sortable: true,
      filterable: true,
      width: '100px',
      render: (value) => <span className="fw-bold fs-5">${value}</span>
    },
    {
      key: 'actions',
      title: t('ordersTable.actions'),
      width: '100px',
      render: (_, order) => (
        ((order.orderType == 0 && hasPermission("Update Order")) ||
          (order.orderType == 1 && hasPermission("Update Ship For Me"))) && (
          <ActionButton
            variant={order.orderType == 0 ? "primary" : "warning"}
            size="sm"
            onClick={() => navigate(`/${order.orderType == 0 ? "order" : "shipforme"}/edit/${order.id}`)}
            tooltip={t('ordersTable.viewDetails')}
            style={{ fontSize: '12px', padding: '4px 8px' }}
          >
            <i className="fa fa-eye"></i>
          </ActionButton>
        )
      )
    }
  ];

  if (loading) {
    return <div className="text-center mt-4">{t('messages.loading')}</div>;
  }

  if (!customer) {
    return <div className="text-center mt-4 text-danger">Customer not found.</div>;
  }

  const breadcrumbs = [
    { label: t('breadcrumbs.customers'), path: '/customers' },
    { label: t('breadcrumbs.view') }
  ];

  return (
    <PageLayout
      title={`${t('customerDetails')} - ${customer?.name || ''} #${id}`}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Customer Information */}
      <div className="card shadow-lg border-0 rounded-3 mb-4">
        <div className="card-body p-4">
          <div className="row g-4">
            <div className="col-md-6">
              <div className="bg-light rounded-3 p-3 h-100">
                <h5 className={`text-primary mb-3 ${isRTL ? 'text-end' : 'text-start'}`}>
                  <i className="fa fa-info-circle me-2"></i>
                  {t('customerInformation')}
                </h5>
                <div className="row g-3">
                  <div className="col-12">
                    <label className={`form-label fw-bold text-muted ${isRTL ? 'text-end' : 'text-start'}`}>
                      {t('fields.name')}:
                    </label>
                    <p className={`mb-2 ${isRTL ? 'text-end' : 'text-start'}`}>{customer.name}</p>
                  </div>
                  <div className="col-12">
                    <label className={`form-label fw-bold text-muted ${isRTL ? 'text-end' : 'text-start'}`}>
                      {t('fields.phone')}:
                    </label>
                    <p className={`mb-2 ${isRTL ? 'text-end' : 'text-start'}`}>{customer.phone}</p>
                  </div>
                  <div className="col-12">
                    <label className={`form-label fw-bold text-muted ${isRTL ? 'text-end' : 'text-start'}`}>
                      {t('fields.email')}:
                    </label>
                    <p className={`mb-2 ${isRTL ? 'text-end' : 'text-start'}`}>{customer.email}</p>
                  </div>
                  <div className="col-12">
                    <label className={`form-label fw-bold text-muted ${isRTL ? 'text-end' : 'text-start'}`}>
                      {t('fields.verifiedAt')}:
                    </label>
                    <p className={`mb-2 ${isRTL ? 'text-end' : 'text-start'}`}>{new Date(customer.createdAt).toLocaleDateString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-')}</p>
                  </div>
                </div>
                {hasPermission("Update Customer") && (
                  <ActionButton
                    variant="warning"
                    size="sm"
                    onClick={() => navigate(`/customers/edit/${id}`)}
                    className="mt-3"
                  >
                    <i className={`fa fa-edit ${isRTL ? 'ms-1' : 'me-1'}`}></i>
                    {t('actions.editProfile')}
                  </ActionButton>
                )}
              </div>
            </div>
            <div className="col-md-6">
              <div className="bg-light rounded-3 p-3 h-100">
                <h5 className={`text-primary mb-3 ${isRTL ? 'text-end' : 'text-start'}`}>
                  <i className="fa fa-map-marker-alt me-2"></i>
                  {t('fields.userAddress')}
                </h5>
                <div className="row g-3">
                  <div className="col-12">
                    <label className={`form-label fw-bold text-muted ${isRTL ? 'text-end' : 'text-start'}`}>
                      {t('fields.country')}:
                    </label>
                    <p className={`mb-2 ${isRTL ? 'text-end' : 'text-start'}`}>{customer.country}</p>
                  </div>
                  <div className="col-12">
                    <label className={`form-label fw-bold text-muted ${isRTL ? 'text-end' : 'text-start'}`}>
                      {t('fields.city')}:
                    </label>
                    <p className={`mb-2 ${isRTL ? 'text-end' : 'text-start'}`}>{customer.city}</p>
                  </div>
                  <div className="col-12">
                    <label className={`form-label fw-bold text-muted ${isRTL ? 'text-end' : 'text-start'}`}>
                      {t('fields.address')}:
                    </label>
                    <p className={`mb-2 ${isRTL ? 'text-end' : 'text-start'}`}>{customer.address}</p>
                  </div>
                  <div className="col-12">
                    <label className={`form-label fw-bold text-muted ${isRTL ? 'text-end' : 'text-start'}`}>
                      {t('fields.address2')}:
                    </label>
                    <p className={`mb-2 ${isRTL ? 'text-end' : 'text-start'}`}>{customer.address2}</p>
                  </div>
                </div>
                {customer.userAddressId && hasPermission("Update Customer") && (
                  <ActionButton
                    variant="primary"
                    size="sm"
                    onClick={() => navigate(`/useraddresses/edit/${customer.userAddressId}`)}
                    className="mt-3"
                  >
                    <i className={`fa fa-pencil ${isRTL ? 'ms-1' : 'me-1'}`}></i>
                    {t('actions.editAddress')}
                  </ActionButton>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* New Order Section */}
        <div className="card shadow-lg border-0 rounded-3 mb-4">
          <div className="card-header bg-gradient-success text-black">
            <h4 className={`mb-0 ${isRTL ? 'text-end' : 'text-start'}`}>
              <i className="fa fa-plus-circle me-2"></i>
              {t('newOrder')}
            </h4>
          </div>
          <div className="card-body p-4">
            {/* Order Type Buttons */}
            <div className="text-center mb-4">
              <div className="btn-group" role="group">
                <ActionButton
                  variant={isShipForMe ? "primary" : "outline-primary"}
                  onClick={() => setIsShipForMe(true)}
                  className="px-4"
                >
                  <i className="fa fa-ship me-2"></i>
                  {t('ship4me')}
                </ActionButton>
                <ActionButton
                  variant={!isShipForMe ? "primary" : "outline-primary"}
                  onClick={() => setIsShipForMe(false)}
                  className="px-4"
                >
                  <i className="fa fa-shopping-cart me-2"></i>
                  {t('buy4me')}
                </ActionButton>
              </div>
            </div>

            {/* Order Form Table */}
            <div className="table-responsive">
              <table className={`table table-striped table-hover ${isRTL ? 'table-rtl' : ''}`}>
                <thead className="bg-primary text-black">
                  <tr>
                    <th className={`${isRTL ? 'text-end' : 'text-start'} py-3`}>
                      <i className={`fa fa-globe ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                      {t('orderForm.country')}
                    </th>
                    <th className={`${isRTL ? 'text-end' : 'text-start'} py-3`}>
                      <i className={`fa fa-link ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                      {t('orderForm.link')}
                    </th>
                    <th className={`${isRTL ? 'text-end' : 'text-start'} py-3`}>
                      <i className={`fa fa-tags ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                      {t('orderForm.options')}
                    </th>
                    <th className={`${isRTL ? 'text-end' : 'text-start'} py-3`}>
                      <i className={`fa fa-sort-numeric-up ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                      {t('orderForm.quantity')}
                    </th>
                    <th className={`${isRTL ? 'text-end' : 'text-start'} py-3`}>
                      <i className={`fa fa-dollar-sign ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                      {t('orderForm.price')}
                    </th>
                    <th className={`${isRTL ? 'text-end' : 'text-start'} py-3`}>
                      <i className={`fa fa-cogs ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                      {t('orderForm.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {formData.map((item, index) => (
                    <tr key={item.key} className="align-middle">
                      <td className={`p-3 ${isRTL ? 'text-end' : 'text-start'}`}>
                        <select
                          className={`form-select form-select-lg border-2 ${isRTL ? 'text-end' : 'text-start'}`}
                          name="originCountryId"
                          value={item.originCountryId || ""}
                          onChange={(e) => handleInputChange(index, e)}
                          dir={isRTL ? 'rtl' : 'ltr'}
                        >
                          <option value="">{t('orderForm.selectCountry')}</option>
                          {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className={`p-3 ${isRTL ? 'text-end' : 'text-start'}`}>
                        <input
                          type="text"
                          className={`form-control form-control-lg border-2 ${isRTL ? 'text-end' : 'text-start'}`}
                          name="link"
                          value={item.link}
                          placeholder={t('orderForm.linkPlaceholder')}
                          onChange={(e) => handleInputChange(index, e)}
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </td>
                      <td className={`p-3 ${isRTL ? 'text-end' : 'text-start'}`}>
                        <input
                          type="text"
                          className={`form-control form-control-lg border-2 ${isRTL ? 'text-end' : 'text-start'}`}
                          name="description"
                          value={item.description}
                          placeholder={t('orderForm.optionsPlaceholder')}
                          onChange={(e) => handleInputChange(index, e)}
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </td>
                      <td className={`p-3 ${isRTL ? 'text-end' : 'text-start'}`}>
                        <div className={`d-flex ${isRTL ? 'justify-content-end' : 'justify-content-start'} align-items-center gap-2`}>
                          <ActionButton
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleQuantityChange(index, -1)}
                            className="rounded-circle"
                            style={{ width: '28px', height: '28px', fontSize: '12px' }}
                          >
                            <i className="fa fa-minus"></i>
                          </ActionButton>
                          <span className={`px-2 fw-bold ${isRTL ? 'text-end' : 'text-start'}`}>{item.quantity}</span>
                          <ActionButton
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleQuantityChange(index, 1)}
                            className="rounded-circle"
                            style={{ width: '28px', height: '28px', fontSize: '12px' }}
                          >
                            <i className="fa fa-plus"></i>
                          </ActionButton>
                        </div>
                      </td>
                      <td className={`p-3 ${isRTL ? 'text-end' : 'text-start'}`}>
                        <input
                          type="number"
                          className={`form-control form-control-lg border-2 ${isRTL ? 'text-end' : 'text-start'}`}
                          name="itemPrice"
                          value={item.itemPrice}
                          placeholder={t('orderForm.pricePlaceholder')}
                          onChange={(e) => handleInputChange(index, e)}
                          min={0}
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                      </td>
                      <td className={`p-3 ${isRTL ? 'text-end' : 'text-start'}`}>
                        <div className={`d-flex ${isRTL ? 'justify-content-end' : 'justify-content-start'} gap-1`}>
                          <ActionButton
                            variant="danger"
                            size="sm"
                            onClick={() => deleteRow(item.key)}
                            tooltip={t('orderForm.deleteRow')}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            <i className="fa fa-trash"></i>
                          </ActionButton>
                          <ActionButton
                            variant="success"
                            size="sm"
                            onClick={addNewRow}
                            tooltip={t('orderForm.addRow')}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            <i className="fa fa-plus"></i>
                          </ActionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Submit button */}
            <div className="text-center mt-4">
              <ActionButton
                variant="primary"
                size="lg"
                onClick={submitOrder}
                disabled={loading}
                className="px-5 py-2"
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className={`fa ${isShipForMe ? 'fa-ship' : 'fa-shopping-cart'} ${isRTL ? 'ms-2' : 'me-2'}`}></i>
                    {t('orderForm.submit')} {isShipForMe ? t('ship4me') : t('buy4me')}
                  </>
                )}
              </ActionButton>
            </div>
          </div>
        </div>

        {/* Orders List */}
        <div className="card shadow-lg border-0 rounded-3 mb-4">
          <div className="card-header bg-gradient-info text-black">
            <h4 className={`mb-0 ${isRTL ? 'text-end' : 'text-start'}`}>
              <i className="fa fa-list-alt me-2"></i>
              {t('customerOrders')}
            </h4>
          </div>
          <div className="card-body">
            <DataTable
              columns={orderColumns}
              data={orders}
              loading={loading}
              searchable={true}
              filterable={true}
              exportable={true}
              refreshable={false}
              emptyMessage={t('ordersTable.noOrders')}
              pageSize={10}
              className="customer-orders-table"
            />
          </div>
        </div>
      </div>

      <div className="text-center mt-4">
        <ActionButton
          variant="outline-primary"
          size="lg"
          onClick={() => navigate('/order/index')}
          className="px-4"
        >
          <i className={`fa fa-list ${isRTL ? 'ms-2' : 'me-2'}`}></i>
          {t('ordersTable.viewAllOrders', 'View All Orders')}
        </ActionButton>
      </div>


    </PageLayout>
  );
};